import { AGGClient } from "src/clients";

import { ProductKey } from "@/components/signup/utils/products";
import { getLocalStorage, setLocalStorage } from "@/utils/localstorage";

export const JIRA_FEATURES_ASSET_CONTROL = "7yKtyC5Ec3V0aQXKE50Qbq";
export const CONFLUENCE_FEATURES_ASSET_CONTROL = "5WpVwJ5Ki0W7djUXQMcT4H";

const GRS_LOCAL_STORAGE_KEY = "ajcbGrsData";

export const BANDITS_PRODUCT = {
    JIRA: "jira",
    CONFLUENCE: "confluence"
} as const;

export type BanditsProduct =
    (typeof BANDITS_PRODUCT)[keyof typeof BANDITS_PRODUCT];

export const CONFLUENCE_SUB_ANCHOR_ASSET_CONTROL = [
    "JzUk3oQfopbEmhR8ONXP8",
    "29N6b3o6vLuo2S24XPBGnP",
    "5ZftPkmfc27ibbzmPzSkPE",
    "1wxWMGtRGQZfFJ3gqtdrSA",
    "5SzCidnsDeHt2bAV0X9izm",
    "6kxU3osFAP8V6dFGau4aW3",
    "5kCs5NZLiEmhTIAx3JpFXw",
    "7vvOZr5lpHNa6aR1PTdbBl"
];
export const CONFLUENCE_SUB_ANCHOR_HEADLINE_CONTROL = [
    "4TFYI2PTQPXpV3HJCmZux6",
    "5SLIVmANacuG4y3kTFzrbD",
    "5kqxmW3jxAt0bGim0IjrY9",
    "1OWBBaXF71fNyZ4ofE6IiM",
    "1YEianjrFDwfKTOnMqcYMe",
    "12YX7o5r4iUAoAIuiAPVjX",
    "18MP6JZJcFJTcftdj7bYYX",
    "5chJUEmBtkOFrtig0hYYjt"
];

const DEFAULT_CONF_BRANDED_SUB_ANCHOR = "default (catch all)";

const normalizeSubAnchor = (subAnchor = "") => subAnchor.trim().toLowerCase();

const CONF_BRANDED_SUB_ANCHORS = [
    "core terms",
    "official site",
    "confluence online",
    "confluence as a solution",
    "confluence wiki",
    "confluence help",
    "confluence reviews",
    "confluence pricing",
    "confluence os",
    "get confluence",
    "confluence for personal use",
    "confluence alternatives",
    "create an account",
    "signup for confluence for free",
    "start a confluence trial",
    "collaboration software",
    "documentation software",
    "spreadsheet",
    "project tracking",
    "how to use confluence",
    "what is confluence",
    "confluence cloud",
    "confluence license",
    "confluence connections",
    "confluence features",
    "whiteboard features",
    "knowledge base",
    "business plan template",
    "confluence program",
    "download confluence",
    "install confluence",
    "login to confluence",
    "confluence server",
    "confluence demo",
    "confluence for design teams",
    "confluence for legal teams",
    "confluence for customer service teams",
    "website creation",
    DEFAULT_CONF_BRANDED_SUB_ANCHOR
];

export const isConfBrandedSubAnchor = (subAnchor = "") =>
    CONF_BRANDED_SUB_ANCHORS.includes(normalizeSubAnchor(subAnchor));

export const isConfluenceSubAnchorAssetControl = (assetId: string) => {
    return CONFLUENCE_SUB_ANCHOR_ASSET_CONTROL.includes(assetId);
};

export const isConfluenceSubAnchorHeadlineControl = (headlineId: string) => {
    return CONFLUENCE_SUB_ANCHOR_HEADLINE_CONTROL.includes(headlineId);
};

export const BANDITS_USE_CASE = {
    ANCHOR_JOURNEY: "wacAnchorJourney",
    ANCHOR_JOURNEY_SUB_ANCHOR: "wacAnchorJourneySubAnchor",
    ANCHOR_JOURNEY_JIRA_BPS: "wacAnchorJourneyJiraBPS",
    ANCHOR_JOURNEY_CONFLUENCE_BRAND_DEFAULT: "wacAjConfSubAnchor"
} as const;

export type BanditsUseCase =
    (typeof BANDITS_USE_CASE)[keyof typeof BANDITS_USE_CASE];

export type BanditsContent = {
    headline?: KeywordAnchorHeadline | null;
    asset?: KeywordAnchorAsset | null;
    recoSessionId: string;
    recoEntityId: string;
};

export const nullBanditsContent: BanditsContent = {
    headline: null,
    asset: null,
    recoSessionId: "",
    recoEntityId: ""
};

interface GrsQueryParams {
    product: BanditsProduct;
    subAnchor?: string;
    subproduct: string;
    locale: string;
    keyword: string;
    channel: string;
    sessionId?: string;
    tenantId?: string;
    orgId?: string;
    anonymousId?: string;
    utmContent?: string;
    useCase: BanditsUseCase;
    utmCampaign?: string;
}

export const getGrsRecommendationsQuery = ({
    product,
    subAnchor = "",
    subproduct = "software",
    locale = "en_US",
    keyword,
    channel = "",
    sessionId = "",
    tenantId = "",
    orgId = "",
    anonymousId = "",
    utmContent = "",
    utmCampaign = "",
    useCase = BANDITS_USE_CASE.ANCHOR_JOURNEY
}: GrsQueryParams) => {
    const resolvedChannel =
        useCase === BANDITS_USE_CASE.ANCHOR_JOURNEY
            ? "paid-search-branded"
            : channel;

    // The check for lower case is new for only Jira BPS use case
    const resolvedSubAnchor =
        useCase === BANDITS_USE_CASE.ANCHOR_JOURNEY_JIRA_BPS
            ? subAnchor.toLowerCase()
            : subAnchor;

    // The check for lower case is new for only Jira BPS use case
    const resolvedKeyword =
        useCase === BANDITS_USE_CASE.ANCHOR_JOURNEY_JIRA_BPS
            ? keyword.toLowerCase()
            : keyword;

    const query = `
query WacAnchorJourney {
    growthRecommendations {
        recommendations(
            context:{
                tenantId: "${tenantId}",
                product: "${product}",
                subproduct: "${subproduct}",
                containers: null,
                sessionId: "${sessionId}",
                locale: "${locale}",
                orgId: "${orgId}",
                workspaceId: null,
                useCase: "${useCase}",
                anonymousId: "${anonymousId}",
                custom: {
                    keyword: "${resolvedKeyword}",
                    channel: "${resolvedChannel}",
                    subAnchor: "${resolvedSubAnchor}"
                    utmContent: "${utmContent}",
                    utmCampaign: "${utmCampaign}",
                }
            },
            first: 1,
            rerank: []
        ) {
            ... on GrowthRecRecommendations {
                data {
                    entityId,
                    reasons,
                },
                context {
                    sessionId
                }
            }
            ... on QueryError {
                identifier
                message
            }
        }
    }
}
`;
    return {
        query,
        variables: {}
    };
};

interface GrowthRec {
    textId: string | null;
    assetId: string | null;
    reasons: string[];
    sessionId: string;
}

interface GrsRecommendationItem {
    entityId: string;
    reasons: string[];
}

interface GrsRecommendationsResponse {
    growthRecommendations?: {
        recommendations?: {
            context?: { sessionId: string };
            data?: GrsRecommendationItem[];
        };
    };
}

/**
 * Parses the response from the GRS recommendations query.
 * @param data The raw data from the GraphQL response's `data` field.
 * @returns An array of growth recommendations.
 */
export const parseGrsRecommendationsData = (
    data: GrsRecommendationsResponse | null
): GrowthRec[] => {
    const recommendations = data?.growthRecommendations?.recommendations;
    const sessionId = recommendations?.context?.sessionId ?? "";

    if (!recommendations) {
        console.error("No recommendations found in GRS response.");
        return [];
    }

    if (Array.isArray(recommendations.data)) {
        return recommendations.data.map((rec) => {
            const { entityId, reasons } = rec;
            let textId: string | null = null;
            let assetId: string | null = null;

            if (typeof entityId === "string") {
                const parts = entityId.split("+");
                for (const part of parts) {
                    if (part.startsWith("text:")) {
                        textId = part.substring("text:".length);
                    } else if (part.startsWith("asset:")) {
                        assetId = part.substring("asset:".length);
                    }
                }
            }

            return { textId, assetId, reasons, sessionId };
        });
    }

    console.error(
        "Unknown response format for GRS recommendations",
        recommendations
    );
    return [];
};

// used for the first Jira and Confluence CB experiment. Currently only used for Conf for en locale.
export const getKeywordAnchorContentQuery = (
    locale = "en-US",
    keyword: string
) => {
    return {
        query: `
query keywordAnchors {
  anchorKeywordMappingCollection(
    locale: "${locale}"
    limit: 1
    where: {keywords_contains_all: ["${keyword}"]}
  ) {
    items {
      headlinesCollection {
        items {
          sys {
            id
          }
          headlineTitleExternal
          headlineSubheading
        }
      }
      assetContentsCollection {
        items {
          sys {
            id
          }
          image1 {
            url
            description
          }
          video1Mp4 {
            url
            description
          }
          video1Webm {
            url
            description
          }
          primaryText1
          secondaryText1
          image2 {
            url
            description
          }
          video2Mp4 {
            url
            description
          }
          video2Webm {
            url
            description
          }
          primaryText2
          secondaryText2
          image3 {
            url
            description
          }
          video3Mp4 {
            url
            description
          }
          video3Webm {
            url
            description
          }
          primaryText3
          secondaryText3
        }
      }
    }
  }
}
`,
        variables: {}
    };
};

export const getConfluenceSubAnchorContentQuery = (
    locale = "en-US",
    anchorGroup: string
) => {
    return {
        query: `
        query ConfluenceSubAnchorContent {
  anchorKeywordMappingCollection(
    locale: "${locale}"
    limit: 1
    where: {anchorGroup_contains: "confluence subAnchor: ${anchorGroup}"}
  ) {
    items {
      anchorGroup
      headlinesCollection {
        items {
          sys {
            id
          }
          headlineTitleExternal
          headlineSubheading
        }
      }
      assetContentsCollection {
        items {
          sys {
            id
          }
          image1OrangeLogic {
            assetUrl
            alt
          }
        }
      }
    }
  }
}
        `,
        variables: {}
    };
};

// used for Jira's BPS v2 experiment
export const getJiraBPSContentQuery = (locale = "en-US", keyword: string) => {
    const lowerCaseKeyword = keyword.toLowerCase();

    return {
        query: `
query JiraBPSContent {
  anchorKeywordMappingCollection(
    locale: "${locale}"
    limit: 1
    where: {anchorGroup_contains: "jira subanchor:",
        keywords_contains_all: ["${lowerCaseKeyword}"]}
  ) {
    items {
      anchorGroup
      headlinesCollection {
        items {
          sys {
            id
          }
          headlineTitleExternal
          headlineSubheading
        }
      }
      assetContentsCollection {
        items {
          sys {
            id
          }
          image1OrangeLogic {
            assetUrl
            alt
          }
        }
      }
    }
  }
}
        `,
        variables: {}
    };
};

// used for Confluence's brand default experiment (bps v2)
export const getConfluenceBrandDefaultContentQuery = (
    locale = "en-US",
    subAnchor: string
) => {
    const lowerCaseSubAnchor = subAnchor.toLowerCase();

    return {
        query: `
query ConfBrandDefault {
  anchorKeywordMappingCollection(
    locale: "${locale}"
    limit: 1
    where: {
      contentfulMetadata: {
        tags: {id_contains_all: "conf-bps-v2"}
      }
      anchorGroup_contains: "${lowerCaseSubAnchor}"
    }
  ) {
    items {
      anchorGroup
      headlinesCollection {
        items {
          sys {
            id
          }
          headlineTitleExternal
          headlineSubheading
        }
      }
      assetContentsCollection {
        items {
          sys {
            id
          }
          image1OrangeLogic {
            assetUrl
            alt
          }
        }
      }
    }
  }
}
        `,
        variables: {}
    };
};

export interface KeywordAnchorHeadline {
    id: string;
    title: string;
    subheading: string;
}

export interface KeywordAnchorAsset {
    id: string;
    image1?: { url?: string; description?: string };
    image1OrangeLogic?: { assetUrl?: string; alt?: string };
    video1Mp4?: { url?: string; description?: string };
    video1Webm?: { url?: string; description?: string };
    primaryText1?: string;
    secondaryText1?: string;
    image2?: { url?: string; description?: string };
    video2Mp4?: { url?: string; description?: string };
    video2Webm?: { url?: string; description?: string };
    primaryText2?: string;
    secondaryText2?: string;
    image3?: { url?: string; description?: string };
    video3Mp4?: { url?: string; description?: string };
    video3Webm?: { url?: string; description?: string };
    primaryText3?: string;
    secondaryText3?: string;
}

export interface ParsedKeywordAnchors {
    headlines: Map<string, KeywordAnchorHeadline>;
    assetContents: Map<string, KeywordAnchorAsset>;
}

interface ContentfulMediaItem {
    url?: string;
    description?: string;
    assetUrl?: string;
    alt?: string;
}

interface ContentfulHeadlineItem {
    sys: { id: string };
    headlineTitleExternal: string;
    headlineSubheading: string;
}

interface ContentfulAssetItem {
    sys: { id: string };
    image1?: ContentfulMediaItem;
    image1OrangeLogic?: ContentfulMediaItem;
    video1Mp4?: ContentfulMediaItem;
    video1Webm?: ContentfulMediaItem;
    primaryText1?: string;
    secondaryText1?: string;
    image2?: ContentfulMediaItem;
    video2Mp4?: ContentfulMediaItem;
    video2Webm?: ContentfulMediaItem;
    primaryText2?: string;
    secondaryText2?: string;
    image3?: ContentfulMediaItem;
    video3Mp4?: ContentfulMediaItem;
    video3Webm?: ContentfulMediaItem;
    primaryText3?: string;
    secondaryText3?: string;
}

interface ContentfulAnchorMappingResponse {
    anchorKeywordMappingCollection?: {
        items?: Array<{
            anchorGroup?: string;
            headlinesCollection?: { items?: ContentfulHeadlineItem[] };
            assetContentsCollection?: { items?: ContentfulAssetItem[] };
        }>;
    };
}

/**
 * Parses the response from the keyword anchors query.
 * @param data The raw data from the GraphQL response's `data` field.
 * @returns An object containing headlines and asset contents.
 */
export const parseKeywordAnchorsData = (
    data: ContentfulAnchorMappingResponse | null
): ParsedKeywordAnchors => {
    const mapping = data?.anchorKeywordMappingCollection?.items?.[0];
    const headlines = new Map<string, KeywordAnchorHeadline>();
    const assetContents = new Map<string, KeywordAnchorAsset>();

    if (!mapping) {
        console.error("No anchor mapping found for the specified keyword.");
        return { headlines, assetContents };
    }

    mapping.headlinesCollection?.items?.forEach((headline) => {
        headlines.set(headline.sys.id, {
            id: headline.sys.id,
            title: headline.headlineTitleExternal,
            subheading: headline.headlineSubheading
        });
    });

    mapping.assetContentsCollection?.items?.forEach((asset) => {
        assetContents.set(asset.sys.id, {
            id: asset.sys.id,
            image1: asset.image1,
            image1OrangeLogic: asset.image1OrangeLogic,
            video1Mp4: asset.video1Mp4,
            video1Webm: asset.video1Webm,
            primaryText1: asset.primaryText1,
            secondaryText1: asset.secondaryText1,
            image2: asset.image2,
            video2Mp4: asset.video2Mp4,
            video2Webm: asset.video2Webm,
            primaryText2: asset.primaryText2,
            secondaryText2: asset.secondaryText2,
            image3: asset.image3,
            video3Mp4: asset.video3Mp4,
            video3Webm: asset.video3Webm,
            primaryText3: asset.primaryText3,
            secondaryText3: asset.secondaryText3
        });
    });

    return { headlines, assetContents };
};

const fetchContentful = async (
    query: string,
    variables: Record<string, unknown>
): Promise<{ data: ContentfulAnchorMappingResponse | null }> => {
    const contentfulUrl = `${process.env["CONTENTFUL_BASE_URL_GQL"]}/content/v1/spaces/${process.env["CONTENTFUL_SPACE_ID_PRODUCT_TEMPLATES"]}/environments/${process.env["CONTENTFUL_ENVIRONMENT_PRODUCT_TEMPLATES"]}`;
    const contentfulAuthKey =
        process.env["CONTENTFUL_DELIVERY_KEY_PRODUCT_TEMPLATES"];

    const response = await fetch(contentfulUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${contentfulAuthKey}`
        },
        credentials: "same-origin",
        body: JSON.stringify({ query, variables })
    });

    if (!response.ok) {
        console.error(
            `Contentful request failed with status ${response.status}`
        );
        return { data: null };
    }

    return response.json();
};

// eslint-disable-next-line complexity
export async function getGrsAndContentfulContent(params: {
    keyword: string;
    locale: string;
    product: BanditsProduct;
    anonymousId: string;
    subAnchor?: string;
    channel?: string;
    utmContent?: string;
    useCase?: BanditsUseCase;
    utmCampaign?: string;
}): Promise<BanditsContent> {
    const aggClient = new AGGClient({
        gatewayUrl:
            process.env["NODE_ENV"] === "production"
                ? "https://www.atlassian.com/gateway/api/graphql"
                : "https://wac.stg.internal.atlassian.com/gateway/api/graphql"
    });

    const requestedSubAnchor = params.subAnchor ?? "";

    // 1. Fetch content from Contentful
    let contentQuery = getKeywordAnchorContentQuery(
        params.locale,
        params.keyword
    );

    // use sub anchor query for sub anchor use case
    if (params.useCase === BANDITS_USE_CASE.ANCHOR_JOURNEY_SUB_ANCHOR) {
        contentQuery = getConfluenceSubAnchorContentQuery(
            params.locale,
            requestedSubAnchor
        );
    }

    // use query for Jira BPS use case
    if (params.useCase === BANDITS_USE_CASE.ANCHOR_JOURNEY_JIRA_BPS) {
        contentQuery = getJiraBPSContentQuery(params.locale, params.keyword);
    }

    const isConfBrandDefaultUseCase =
        params.useCase ===
        BANDITS_USE_CASE.ANCHOR_JOURNEY_CONFLUENCE_BRAND_DEFAULT;

    const resolvedSubAnchor =
        isConfBrandDefaultUseCase &&
        !isConfBrandedSubAnchor(requestedSubAnchor.toLowerCase())
            ? DEFAULT_CONF_BRANDED_SUB_ANCHOR
            : requestedSubAnchor.toLowerCase();

    if (isConfBrandDefaultUseCase) {
        contentQuery = getConfluenceBrandDefaultContentQuery(
            params.locale,
            resolvedSubAnchor
        );
    }

    const contentResult = await fetchContentful(
        contentQuery.query,
        contentQuery.variables
    );

    const { headlines, assetContents } = parseKeywordAnchorsData(
        contentResult.data
    );

    // 2 Fetch recommendations from GRS
    const grsQuery = getGrsRecommendationsQuery({
        keyword: params.keyword,
        subAnchor: resolvedSubAnchor,
        product: params.product,
        locale: params.locale,
        subproduct: "software",
        channel: params.channel ?? "",
        anonymousId: params.anonymousId,
        useCase: params.useCase ?? BANDITS_USE_CASE.ANCHOR_JOURNEY,
        utmContent: params.utmContent ?? "",
        utmCampaign: params.utmCampaign ?? ""
    });
    const grsResult = await aggClient.query(grsQuery.query, grsQuery.variables);
    const recommendations = parseGrsRecommendationsData(
        grsResult.data as GrsRecommendationsResponse
    );

    // get top ranked recommendation
    const recommendation = recommendations[0];

    const results: BanditsContent = { ...nullBanditsContent };

    if (!recommendation) {
        console.error("No recommendation from GRS");
        return results;
    }

    // 3. Find the matching content
    const headline = headlines.get(recommendation.textId ?? "");
    const asset = assetContents.get(recommendation.assetId ?? "");
    const recoSessionId = recommendation.sessionId;

    if (!headline && !asset) {
        console.error(
            "Could not match GRS recommendation to Contentful content"
        );
        return results;
    }

    results.headline = headline ?? null;
    results.asset = asset ?? null;
    results.recoSessionId = recoSessionId;
    results.recoEntityId = `text:${headline?.id}+asset:${asset?.id}`;

    return results;
}

export const setGrsDataToLocalStorage = (
    data: {
        recoSessionId: string;
        recoEntityId: string;
    },
    productKey: ProductKey
) => {
    try {
        const existingData = JSON.parse(
            getLocalStorage(GRS_LOCAL_STORAGE_KEY) || "{}"
        );

        const updatedData = {
            ...existingData,
            [productKey]: {
                recoSessionId: data.recoSessionId,
                recoEntityId: data.recoEntityId
            }
        };

        setLocalStorage(GRS_LOCAL_STORAGE_KEY, JSON.stringify(updatedData));
    } catch {
        // If parsing fails, create new structure with just this product
        setLocalStorage(
            GRS_LOCAL_STORAGE_KEY,
            JSON.stringify({
                [productKey]: {
                    recoSessionId: data.recoSessionId,
                    recoEntityId: data.recoEntityId
                }
            })
        );
    }
};

export const getGrsDataFromLocalStorage = (
    productKey?: string
): {
    recoSessionId?: string;
    recoEntityId?: string;
} => {
    if (
        productKey === undefined ||
        !Object.values(ProductKey).includes(productKey as ProductKey)
    ) {
        return {};
    }

    try {
        const data = JSON.parse(getLocalStorage(GRS_LOCAL_STORAGE_KEY) || "{}");
        return data[productKey] || {};
    } catch {
        return {};
    }
};
